import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { formatUZS } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Search, Package, Edit2, Trash2, FolderTree, Loader2, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import ProductFormFields from '@/components/products/ProductFormFields';
import { MediaUploadField } from '@/components/media/MediaUploadField';
import GlobalProducts from '@/components/admin/GlobalProducts';
import AssignGlobalProducts from '@/components/AssignGlobalProducts';
import {
  emptyProductForm, ProductFormState, productToForm, formToPayload, isProductFormValid,
} from '@/lib/product-schema';
import { apiErrorMessage } from '@/lib/api-error';
import { useTranslation } from 'react-i18next';
import { intlLocale } from './adminLocale';

type Product = components['schemas']['ProductResource'];
type Category = components['schemas']['CategoryResource'];
type GlobalCategory = components['schemas']['GlobalCategoryResource'];
type Branch = components['schemas']['BranchResource'];
type Stock = components['schemas']['StockResource'];

const slugifyCode = (v: string) =>
  v.toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const emptyForm = emptyProductForm;

export default function AdminProducts() {
  const { admin } = useAdminAuth();
  const isOwner = admin?.role === 'platform_owner';
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Global categories
  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState<GlobalCategory | null>(null);
  const [catCode, setCatCode] = useState('');
  const [catName, setCatName] = useState('');
  const [catIconUrl, setCatIconUrl] = useState('');
  const [catImageUrl, setCatImageUrl] = useState('');
  const [catStatus, setCatStatus] = useState('active');
  const [catOrder, setCatOrder] = useState('0');
  const [catKeywords, setCatKeywords] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [catSearch, setCatSearch] = useState('');
  const [catStatusFilter, setCatStatusFilter] = useState('all');

  const { data: catData, isLoading: catLoading } = useQuery({ queryKey: ['/global-categories'] });
  const categories: GlobalCategory[] = catData?.data ?? [];

  const { data: merchantsData } = useQuery({ queryKey: ['/admin/merchants'] });
  const merchants: { id: number; name: string }[] = merchantsData?.data ?? [];
  const [selectedMerchant, setSelectedMerchant] = useState<number | ''>('');

  const { data: productsData, isLoading: prodLoading } = useQuery({
    queryKey: ['/products', { params: { query: { merchant_id: selectedMerchant || undefined } as never } }],
    enabled: !!selectedMerchant,
  });
  const products: Product[] = productsData?.data ?? [];

  const { data: merchantCatData } = useQuery({
    queryKey: ['/categories', { params: { query: { merchant_id: selectedMerchant || undefined } as never } }],
    enabled: !!selectedMerchant,
  });
  const merchantCategories: Category[] = merchantCatData?.data ?? [];

  const { data: branchesData } = useQuery({
    queryKey: ['/branches', { params: { query: { merchant_id: selectedMerchant || undefined } as never } }],
    enabled: !!selectedMerchant,
  });
  const stockBranches: Branch[] = branchesData?.data ?? [];

  const { data: stocksData, isLoading: stockLoading } = useQuery({
    queryKey: ['stocks-for-branches', stockBranches.map(b => b.id)],
    enabled: stockBranches.length > 0,
    queryFn: async () => {
      const results = await Promise.all(stockBranches.map(b =>
        apiClient.GET('/stocks', { params: { query: { branch_id: b.id } as never } }),
      ));
      return results.flatMap(r => r.data?.data ?? []) as Stock[];
    },
  });
  const stocks: Stock[] = stocksData ?? [];

  const [prodSearch, setProdSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stockSearch, setStockSearch] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');

  // Product form
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [formTouched, setFormTouched] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Merchant category manager
  const [showCatManager, setShowCatManager] = useState(false);
  const [mCatDialog, setMCatDialog] = useState(false);
  const [mCatEditId, setMCatEditId] = useState<number | null>(null);
  const [mCatName, setMCatName] = useState('');
  const [mCatSaving, setMCatSaving] = useState(false);
  const [mCatDeleteId, setMCatDeleteId] = useState<number | null>(null);
  const [mCatDeleting, setMCatDeleting] = useState(false);

  // Override
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideProduct, setOverrideProduct] = useState<Product | null>(null);
  const [overrideMurabaha, setOverrideMurabaha] = useState<boolean | null>(null);
  const [overrideCategoryId, setOverrideCategoryId] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  const invalidateProducts = () => queryClient.invalidateQueries({ queryKey: ['/products'] });
  const invalidateMerchantCats = () => queryClient.invalidateQueries({ queryKey: ['/categories'] });
  const invalidateGlobalCats = () => queryClient.invalidateQueries({ queryKey: ['/global-categories'] });

  // Global category CRUD
  const openCreateCat = () => {
    setEditingCat(null);
    setCatCode(''); setCatName(''); setCatStatus('active');
    setCatIconUrl(''); setCatImageUrl('');
    setCatOrder(String((categories.length || 0) + 1));
    setCatKeywords('');
    setCatDialog(true);
  };
  const openEditCat = (c: GlobalCategory) => {
    setEditingCat(c);
    setCatCode(c.code || ''); setCatName(c.name); setCatStatus(c.status);
    setCatIconUrl(c.icon_url || ''); setCatImageUrl(c.image_url || '');
    setCatOrder(String(c.display_order ?? 0));
    setCatKeywords((c.search_keywords || []).join(', '));
    setCatDialog(true);
  };

  const saveCat = async () => {
    const name = catName.trim();
    const code = catCode.trim();
    if (!name) { toast.error(t('adm_products.enterName')); return; }
    if (!code) { toast.error(t('adm_products.enterCode')); return; }
    if (!/^[a-z0-9_]+$/.test(code)) { toast.error(t('adm_products.codeFormat')); return; }
    setCatSaving(true);
    try {
      const payload = {
        name, code, status: catStatus,
        display_order: Number.isFinite(parseInt(catOrder, 10)) ? parseInt(catOrder, 10) : 0,
        search_keywords: catKeywords.split(',').map(k => k.trim()).filter(Boolean),
      };
      if (editingCat) {
        const { error } = await apiClient.PATCH('/admin/global-categories/{globalCategory}', {
          params: { path: { globalCategory: editingCat.id } }, body: payload,
        });
        if (error) throw error;
        toast.success(t('adm_products.catUpdated'));
      } else {
        const { error } = await apiClient.POST('/admin/global-categories', { body: payload });
        if (error) throw error;
        toast.success(t('adm_products.catCreated'));
      }
      setCatDialog(false);
      invalidateGlobalCats();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setCatSaving(false); }
  };

  const filteredCategories = categories.filter(c => {
    const q = catSearch.trim().toLowerCase();
    const matchQ = !q || c.name.toLowerCase().includes(q) || (c.code || '').toLowerCase().includes(q);
    const matchS = catStatusFilter === 'all' || c.status === catStatusFilter;
    return matchQ && matchS;
  });

  // Product CRUD
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); setFormTouched(false); };

  const openEdit = (p: Product) => {
    setEditingId(p.id);
    setForm(productToForm(p));
    setFormTouched(false);
    setShowForm(true);
  };

  const saveProduct = async () => {
    setFormTouched(true);
    if (!isProductFormValid(form)) { toast.error(t('adm_products.allFieldsRequired')); return; }
    setFormSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        const { error } = await apiClient.PATCH('/products/{product}', {
          params: { path: { product: editingId } }, body: payload,
        });
        if (error) throw error;
        toast.success(t('adm_products.prodUpdated'));
      } else {
        const { error } = await apiClient.POST('/products', {
          // FR-98: as Lovable, the admin sets «Asl narxi» only when creating.
          body: { ...payload, merchant_id: selectedMerchant as number, price: parseFloat(form.price) || 0 } as never,
        });
        if (error) throw error;
        toast.success(t('adm_products.prodAdded'));
      }
      closeForm();
      invalidateProducts();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setFormSaving(false); }
  };

  const deleteProduct = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const { error } = await apiClient.DELETE('/products/{product}', { params: { path: { product: deleteId } } });
      if (error) throw error;
      toast.success(t('adm_products.prodDeleted'));
      setDeleteId(null);
      invalidateProducts();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setDeleting(false); }
  };

  // Merchant category CRUD
  const closeMCatDialog = () => { setMCatDialog(false); setMCatEditId(null); setMCatName(''); };

  const saveMCat = async () => {
    if (!mCatName.trim()) { toast.error(t('adm_products.enterName')); return; }
    setMCatSaving(true);
    try {
      if (mCatEditId) {
        const { error } = await apiClient.PATCH('/categories/{category}', {
          params: { path: { category: mCatEditId } }, body: { name: mCatName.trim() },
        });
        if (error) throw error;
        toast.success(t('adm_products.catUpdated'));
      } else {
        const { error } = await apiClient.POST('/categories', {
          body: { name: mCatName.trim(), merchant_id: selectedMerchant as number } as never,
        });
        if (error) throw error;
        toast.success(t('adm_products.catAdded'));
      }
      closeMCatDialog();
      invalidateMerchantCats();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setMCatSaving(false); }
  };

  const deleteMCat = async () => {
    if (!mCatDeleteId) return;
    setMCatDeleting(true);
    try {
      const { error } = await apiClient.DELETE('/categories/{category}', { params: { path: { category: mCatDeleteId } } });
      if (error) throw error;
      toast.success(t('adm_products.catDeleted'));
      setMCatDeleteId(null);
      invalidateMerchantCats();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setMCatDeleting(false); }
  };

  // Override
  const openOverride = (p: Product) => {
    setOverrideProduct(p);
    setOverrideMurabaha(p.override?.is_murabaha_eligible_override ?? null);
    setOverrideCategoryId(p.override?.category_id ? String(p.override.category_id) : '');
    setOverrideReason(p.override?.reason || '');
    setOverrideDialog(true);
  };

  const saveOverride = async () => {
    if (!overrideProduct) return;
    setOverrideSaving(true);
    try {
      const { error } = await apiClient.PUT('/products/{product}/override', {
        params: { path: { product: overrideProduct.id } },
        body: {
          category_id: overrideCategoryId ? Number(overrideCategoryId) : null,
          is_murabaha_eligible_override: overrideMurabaha,
          reason: overrideReason.trim(),
        },
      });
      if (error) throw error;
      toast.success(t('adm_products.overrideSaved'));
      setOverrideDialog(false);
      invalidateProducts();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setOverrideSaving(false); }
  };

  const categoryNames: string[] = merchantCategories.map(c => c.name);

  const filtered = products.filter(p => {
    const matchSearch = !prodSearch || p.name?.toLowerCase().includes(prodSearch.toLowerCase()) || p.sku?.includes(prodSearch);
    const matchCat = selectedCategory === 'all' || p.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const productMap = Object.fromEntries(products.map(p => [p.id, p]));
  const branchMap = Object.fromEntries(stockBranches.map(b => [b.id, b]));

  const filteredStocks = stocks.filter(s => {
    const p = productMap[s.product_id];
    if (!p) return false;
    const matchBranch = selectedBranch === 'all' || s.branch_id === Number(selectedBranch);
    const matchSearch = !stockSearch || p.name?.toLowerCase().includes(stockSearch.toLowerCase()) || p.sku?.includes(stockSearch);
    return matchBranch && matchSearch;
  });
  const lowStock = filteredStocks.filter(s => s.quantity > 0 && s.quantity <= 5).length;
  const outOfStock = filteredStocks.filter(s => s.quantity <= 0).length;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('adm_products.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('adm_products.subtitle')}</p>
      </div>

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">{t('adm_products.tabProducts')}</TabsTrigger>
          <TabsTrigger value="inventory">{t('adm_products.tabInventory')}</TabsTrigger>
          <TabsTrigger value="categories">{t('adm_products.tabCategories')}</TabsTrigger>
          <TabsTrigger value="global-products">{t('adm_products.tabGlobalProducts')}</TabsTrigger>
          <TabsTrigger value="assign-products">{t('adm_products.tabAssign')}</TabsTrigger>
        </TabsList>

        {/* PRODUCTS TAB */}
        <TabsContent value="products" className="space-y-4">
          <div className="flex gap-3 items-center flex-wrap">
            <Select value={selectedMerchant ? String(selectedMerchant) : '__none__'} onValueChange={v => { setSelectedMerchant(v === '__none__' ? '' : Number(v)); setSelectedCategory('all'); setProdSearch(''); }}>
              <SelectTrigger className="w-[280px]"><SelectValue placeholder={t('adm_products.selectMerchant')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('adm_products.selectMerchant')}</SelectItem>
                {merchants.map(m => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedMerchant && (
              <>
                <div className="relative flex-1 max-w-sm">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder={t('adm_products.searchPlaceholder')} value={prodSearch} onChange={e => setProdSearch(e.target.value)} className="pl-9" />
                </div>
                <Button size="sm" variant="outline" onClick={() => setShowCatManager(true)}>
                  <FolderTree size={14} className="mr-1" /> {t('adm_products.categories')}
                </Button>
                <Button size="sm" onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(true); }}>
                  <Plus size={14} className="mr-1" /> {t('adm_products.add')}
                </Button>
              </>
            )}
          </div>

          {selectedMerchant && categoryNames.length > 0 && (
            <div className="flex gap-1 overflow-x-auto">
              <button onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                {t('adm_common.all')}
              </button>
              {categoryNames.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                  {cat}
                </button>
              ))}
            </div>
          )}

          {!selectedMerchant ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Package className="mx-auto mb-2 h-10 w-10 opacity-30" />
              <p>{t('adm_products.selectMerchantForProducts')}</p>
            </CardContent></Card>
          ) : prodLoading ? (
            <div className="text-muted-foreground py-8 text-center">{t('adm_common.loading')}</div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_name')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_brand')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_attribute')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_mxikCode')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_mxikName')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_barcode')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_category')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_unit')}</th>
                    <th className="text-right text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_price')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_murabaha')}</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={12} className="p-8 text-center text-muted-foreground">{t('adm_products.productNotFound')}</td></tr>
                  ) : (
                    filtered.map(p => (
                      <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="p-3 text-sm text-foreground font-medium max-w-[220px] truncate" title={p.name}>{p.name}</td>
                        <td className="p-3 text-sm text-muted-foreground font-mono">{p.sku}</td>
                        <td className="p-3 text-sm text-muted-foreground max-w-[120px] truncate" title={p.brend_nomi || ''}>{p.brend_nomi || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground max-w-[140px] truncate" title={p.atribut_nomi || ''}>{p.atribut_nomi || '—'}</td>
                        <td className="p-3 text-xs text-muted-foreground font-mono">{p.mhik || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground max-w-[180px] truncate" title={p.mxik_nomi || ''}>{p.mxik_nomi || '—'}</td>
                        <td className="p-3 text-xs text-muted-foreground font-mono">{p.barcode || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground">{p.category || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground">{p.unit}</td>
                        <td className="p-3 text-sm text-right font-mono">
                          {Number(p.sale_price) > 0 ? (
                            <span className="text-warning font-medium">{formatUZS(Number(p.sale_price))}</span>
                          ) : (
                            <span className="text-foreground">{formatUZS(Number(p.price || 0))}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant={p.is_murabaha_eligible ? 'default' : 'secondary'}>
                            {p.is_murabaha_eligible ? t('adm_products.yes') : t('adm_products.no')}
                          </Badge>
                          {p.override && <span className="text-[10px] text-yellow-600 ml-1">{t('adm_products.overrideLower')}</span>}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"><Edit2 size={14} /></button>
                            <button onClick={() => openOverride(p)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('adm_products.override')}><Pencil size={14} /></button>
                            <button onClick={() => setDeleteId(p.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* INVENTORY TAB */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="flex gap-3 items-center flex-wrap">
            <Select value={selectedMerchant ? String(selectedMerchant) : '__none__'} onValueChange={v => { setSelectedMerchant(v === '__none__' ? '' : Number(v)); setSelectedBranch('all'); setStockSearch(''); }}>
              <SelectTrigger className="w-[280px]"><SelectValue placeholder={t('adm_products.selectMerchant')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('adm_products.selectMerchant')}</SelectItem>
                {merchants.map(m => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedMerchant && stockBranches.length > 0 && (
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('adm_products.allBranches')}</SelectItem>
                  {stockBranches.map(b => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {selectedMerchant && (
              <div className="relative flex-1 max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder={t('adm_products.searchPlaceholder')} value={stockSearch} onChange={e => setStockSearch(e.target.value)} className="pl-9" />
              </div>
            )}
          </div>

          {!selectedMerchant ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Warehouse className="mx-auto mb-2 h-10 w-10 opacity-30" />
              <p>{t('adm_products.selectMerchantForInventory')}</p>
            </CardContent></Card>
          ) : stockLoading ? (
            <div className="text-muted-foreground py-8 text-center">{t('adm_common.loading')}</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="py-3 px-4">
                  <p className="text-xs text-muted-foreground">{t('adm_products.totalPositions')}</p>
                  <p className="text-xl font-bold text-foreground">{filteredStocks.length}</p>
                </CardContent></Card>
                <Card><CardContent className="py-3 px-4">
                  <p className="text-xs text-muted-foreground">{t('adm_products.lowStock')}</p>
                  <p className="text-xl font-bold text-warning">{lowStock}</p>
                </CardContent></Card>
                <Card><CardContent className="py-3 px-4">
                  <p className="text-xs text-muted-foreground">{t('adm_products.outOfStock')}</p>
                  <p className="text-xl font-bold text-destructive">{outOfStock}</p>
                </CardContent></Card>
              </div>

              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                      <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_product')}</th>
                      <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_category')}</th>
                      <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_branch')}</th>
                      <th className="text-right text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_quantity')}</th>
                      <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_unit')}</th>
                      <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_products.th_updated')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStocks.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{t('adm_products.noStock')}</td></tr>
                    ) : (
                      filteredStocks.map(s => {
                        const p = productMap[s.product_id];
                        const b = branchMap[s.branch_id];
                        return (
                          <tr key={s.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                            <td className="p-3 text-sm text-muted-foreground font-mono">{p?.sku}</td>
                            <td className="p-3 text-sm text-foreground font-medium">{p?.name}</td>
                            <td className="p-3 text-sm text-muted-foreground">{p?.category || '—'}</td>
                            <td className="p-3 text-sm text-muted-foreground">{b?.name || '—'}</td>
                            <td className="p-3 text-sm text-right font-mono">
                              <span className={s.quantity <= 0 ? 'text-destructive font-bold' : s.quantity <= 5 ? 'text-warning font-medium' : 'text-foreground'}>
                                {s.quantity}
                              </span>
                            </td>
                            <td className="p-3 text-sm text-muted-foreground">{p?.unit || t('adm_products.piece')}</td>
                            <td className="p-3 text-xs text-muted-foreground">{new Date(s.updated_at).toLocaleDateString(intlLocale())}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* CATEGORIES TAB */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={catSearch} onChange={e => setCatSearch(e.target.value)} placeholder={t('adm_products.catSearchPlaceholder')} className="pl-9 w-64" />
              </div>
              <Select value={catStatusFilter} onValueChange={setCatStatusFilter}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('adm_products.allStatuses')}</SelectItem>
                  <SelectItem value="active">{t('adm_pricing.active')}</SelectItem>
                  <SelectItem value="inactive">{t('adm_pricing.inactive')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">{filteredCategories.length} / {categories.length}</p>
            </div>
            {isOwner && (
              <Button size="sm" onClick={openCreateCat}><Plus size={16} className="mr-1" /> {t('adm_products.new')}</Button>
            )}
          </div>
          {catLoading ? (
            <div className="text-muted-foreground py-8">{t('adm_common.loading')}</div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">{t('adm_products.order')}</TableHead>
                    <TableHead className="w-14">{t('adm_products.image')}</TableHead>
                    <TableHead>{t('adm_products.code')}</TableHead>
                    <TableHead>{t('adm_products.th_name')}</TableHead>
                    <TableHead>{t('adm_products.keywords')}</TableHead>
                    <TableHead>{t('adm_merchants.status')}</TableHead>
                    {isOwner && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCategories.length === 0 ? (
                    <TableRow><TableCell colSpan={isOwner ? 7 : 6} className="text-center text-muted-foreground py-8">{t('adm_products.catNotFound')}</TableCell></TableRow>
                  ) : filteredCategories.map(c => {
                    const kw = c.search_keywords || [];
                    const img = c.icon_url || c.image_url;
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="text-sm text-muted-foreground font-mono">{c.display_order ?? 0}</TableCell>
                        <TableCell>
                          {img ? (
                            <img src={img} alt={c.name} className="h-8 w-8 rounded object-cover border border-border" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">{c.code || '—'}</TableCell>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">
                          {kw.length ? kw.slice(0, 3).join(', ') + (kw.length > 3 ? ` +${kw.length - 3}` : '') : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={c.status === 'active' ? 'default' : 'secondary'}>
                            {c.status === 'active' ? t('adm_pricing.active') : t('adm_pricing.inactive')}
                          </Badge>
                        </TableCell>
                        {isOwner && (
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => openEditCat(c)}><Pencil size={14} /></Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* GLOBAL PRODUCTS TAB */}
        <TabsContent value="global-products">
          <GlobalProducts />
        </TabsContent>

        {/* ASSIGN TO MERCHANT TAB */}
        <TabsContent value="assign-products">
          <AssignGlobalProducts merchants={merchants} />
        </TabsContent>
      </Tabs>

      {/* Global category dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingCat ? t('adm_products.editCategory') : t('adm_products.newCategory')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('adm_products.nameRequired')}</Label>
                <Input
                  value={catName}
                  onChange={e => setCatName(e.target.value)}
                  // As Lovable: a new category gets its code from the name unless one was typed.
                  onBlur={() => { if (!editingCat && !catCode.trim() && catName.trim()) setCatCode(slugifyCode(catName)); }}
                  placeholder={t('adm_products.categoryName')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adm_products.codeRequired')}</Label>
                <Input value={catCode} onChange={e => setCatCode(e.target.value.toLowerCase())} placeholder={t('adm_products.codePlaceholder')} className="font-mono" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('adm_products.orderNumber')}</Label>
                <Input type="number" value={catOrder} onChange={e => setCatOrder(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t('adm_merchants.status')}</Label>
                <Select value={catStatus} onValueChange={setCatStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('adm_pricing.active')}</SelectItem>
                    <SelectItem value="inactive">{t('adm_pricing.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <MediaUploadField
                kind="category_icon"
                targetId={editingCat ? String(editingCat.id) : null}
                value={catIconUrl}
                onChange={(url) => { setCatIconUrl(url); invalidateGlobalCats(); }}
                label={t('adm_products.icon')}
                shape="square"
                disabled={!editingCat}
                disabledReason={t('adm_products.saveCategoryFirst')}
              />
              <MediaUploadField
                kind="category_image"
                targetId={editingCat ? String(editingCat.id) : null}
                value={catImageUrl}
                onChange={(url) => { setCatImageUrl(url); invalidateGlobalCats(); }}
                label={t('adm_products.image')}
                disabled={!editingCat}
                disabledReason={t('adm_products.saveCategoryFirst')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('adm_products.searchKeywords')}</Label>
              <Textarea value={catKeywords} onChange={e => setCatKeywords(e.target.value)} placeholder={t('adm_products.keywordsPlaceholder')} rows={3} />
              <p className="text-xs text-muted-foreground">{t('adm_products.commaHint')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveCat} disabled={catSaving}>{catSaving ? t('adm_common.saving') : t('adm_common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) closeForm(); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? t('adm_products.editProduct') : t('adm_products.newProduct')}</DialogTitle></DialogHeader>
          <ProductFormFields form={form} setForm={setForm} categories={categoryNames} isEditing={!!editingId} showErrors={formTouched} showOriginalPrice={!editingId} />
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveProduct} disabled={formSaving}>
              {formSaving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('adm_common.saving')}</> : t('adm_common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Delete */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_products.deleteProduct')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('adm_products.deleteProductConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>{t('adm_common.cancel')}</Button>
            <Button variant="destructive" onClick={deleteProduct} disabled={deleting}>
              {deleting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              {t('adm_pricing.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merchant Category Manager Dialog */}
      <Dialog open={showCatManager} onOpenChange={setShowCatManager}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderTree size={18} /> {t('adm_products.categories')}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {merchantCategories.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('adm_products.catsNotFound')}</p>
            ) : (
              merchantCategories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <span className="text-sm font-medium text-foreground">{cat.name}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setMCatEditId(cat.id); setMCatName(cat.name); setMCatDialog(true); }} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => setMCatDeleteId(cat.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <Button size="sm" variant="outline" className="w-full" onClick={() => { setMCatEditId(null); setMCatName(''); setMCatDialog(true); }}>
            <Plus size={14} className="mr-1" /> {t('adm_products.newCategory')}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={mCatDialog} onOpenChange={v => { if (!v) closeMCatDialog(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{mCatEditId ? t('adm_products.editCategory') : t('adm_products.newCategory')}</DialogTitle></DialogHeader>
          <Input placeholder={t('adm_products.categoryName')} value={mCatName} onChange={e => setMCatName(e.target.value)} autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={closeMCatDialog}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveMCat} disabled={!mCatName.trim() || mCatSaving}>{mCatSaving ? t('adm_common.saving') : t('adm_common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mCatDeleteId} onOpenChange={() => setMCatDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_products.deleteCategory')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('adm_products.deleteCategoryConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMCatDeleteId(null)}>{t('adm_common.cancel')}</Button>
            <Button variant="destructive" onClick={deleteMCat} disabled={mCatDeleting}>{mCatDeleting ? t('adm_pricing.deleting') : t('adm_pricing.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override dialog */}
      <Dialog open={overrideDialog} onOpenChange={setOverrideDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_products.overrideTitle', { name: overrideProduct?.name })}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('adm_products.globalCategory')}</Label>
              <Select value={overrideCategoryId || '__none__'} onValueChange={v => setOverrideCategoryId(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder={t('adm_products.selectOptional')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('adm_merchants.trust_none')}</SelectItem>
                  {categories.filter(c => c.status === 'active').map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Label>{t('adm_products.murabahaEligible')}</Label>
              <div className="flex gap-2">
                {[{ label: t('adm_products.originalValue'), val: null }, { label: t('adm_products.yes'), val: true }, { label: t('adm_products.no'), val: false }].map(opt => (
                  <Button
                    key={String(opt.val)}
                    size="sm"
                    variant={overrideMurabaha === opt.val ? 'default' : 'outline'}
                    onClick={() => setOverrideMurabaha(opt.val)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('adm_merchantDetail.reason')}</Label>
              <Textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder={t('adm_products.overrideReasonPlaceholder')} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={saveOverride} disabled={overrideSaving}>{overrideSaving ? t('adm_common.saving') : t('adm_common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
